import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ScatterChart, Scatter, ReferenceLine,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtPct } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { IconAlertTriangle } from "../components/icons";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import { KpiTile, SparkLine } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import type { FleetOverviewDto } from "@bi/types";
import { TollCoverageNotice, SEM_EXTRATO_TITLE } from "../components/TollCoverageNotice";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

const CAT_COLOR: Record<string, string> = { Combustível: "var(--brand)", Manutenção: "#DAA72B", Pneus: "#3B82F6" };
const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctOfTotal = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}% do custo total` : "—");

/**
 * Célula de custo da tabela "Ranking detalhado por placa": valor em R$ + percentual
 * sobre o faturamento DA PRÓPRIA placa (— quando não há faturamento, evita ÷0).
 */
function CostCell({ value, revenue, last, semExtrato }: { value: number; revenue: number; last?: boolean; semExtrato?: boolean }) {
  if (semExtrato) {
    return (
      <td className={`${last ? "px-5" : "px-3"} py-2.5 text-right`} title={SEM_EXTRATO_TITLE}>
        <p className="text-gray-400 text-xs">sem extrato</p>
      </td>
    );
  }
  const pct = revenue > 0 ? `${fmtPct((value / revenue) * 100)} do faturamento` : "—";
  return (
    <td className={`${last ? "px-5" : "px-3"} py-2.5 text-right`}>
      <p className="text-gray-700">{fmtBRL(value)}</p>
      <p className="text-xs text-gray-400">{pct}</p>
    </td>
  );
}

export function FleetOverviewPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "fleet", from, to, filters.comparison],
    queryFn: () =>
      api.get<FleetOverviewDto>(
        `/dashboards/transport/fleet?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const p = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. mês anterior";
  const totalDelta = computeDelta(s?.totalCost, p?.totalCost, { direction: "higher_is_worse" });

  const composition = data?.composition ?? [];
  const costTrend = data?.costTrend ?? [];
  // Sparklines dos KPIs: só os 4 que têm quebra mensal própria no DTO
  // (`costTrend`). Custo/km e Veículo Mais Caro não têm série mensal — sem
  // sparkline neles, pra não inventar visual sem dado real por trás.
  const monthlyTotal       = costTrend.map((c) => c.total);
  const monthlyFuel        = costTrend.map((c) => c.fuel);
  const monthlyMaintenance = costTrend.map((c) => c.maintenance);
  const monthlyTires       = costTrend.map((c) => c.tires);
  const vehicles = data?.vehicles ?? [];
  // A lista chega inteira e já ordenada pelo servidor (maior faturamento
  // primeiro); o corte aqui é só de exibição.
  const list = useProgressiveList(vehicles);
  // Barra de participação do faturamento na tabela: relativa ao maior
  // faturamento da lista INTEIRA (não só da página visível), pra escala não
  // mudar conforme mais linhas forem carregadas com "carregar mais".
  const maxRevenue = Math.max(...vehicles.map((v) => v.revenue), 1);
  const alerts = data?.alerts ?? [];

  const fleetCpk = s?.costPerKm ?? 0;
  // `vehicles` vem ordenada por FATURAMENTO; o gráfico é "por custo", então
  // reordena aqui. Cortar a lista direto mostrava as 10 placas que mais faturam.
  const top10 = [...vehicles]
    .filter((v) => v.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
    .map((v) => ({ plate: v.plate, total: v.total }));
  // km mínimo igual ao `FLEET_COST_PER_KM_MIN_KM` do backend (mesmo critério já
  // usado nos alertas de custo/km): com km muito baixo o denominador não
  // representa uso real e um único veículo (ex. 4km rodados) distorce a escala
  // do gráfico inteiro, esmagando todos os outros pontos perto do zero.
  const SCATTER_MIN_KM = 1000;
  const scatter = vehicles.filter((v) => v.km >= SCATTER_MIN_KM && v.costPerKm > 0)
    .map((v) => ({ x: v.revenue, y: v.costPerKm, plate: v.plate }));
  const scatterBelow = scatter.filter((d) => d.y <= fleetCpk);
  const scatterAbove = scatter.filter((d) => d.y > fleetCpk);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Visão Geral"
        subtitle="Custo consolidado da frota: combustível, manutenção, pneus e rentabilidade por veículo"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "frota.custo-total", from, to, expected: s?.totalCost, title: "Custo Total da Frota" }}>
          <KpiTile label="Custo Total da Frota" value={s ? fmtBRL(s.totalCost) : "—"}
            delta={totalDelta} deltaLabel={cmp}
            visual={monthlyTotal.length >= 2 ? <SparkLine values={monthlyTotal} label="Custo total por mês" /> : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "frota.combustivel", from, to, expected: s?.fuelCost, title: "Combustível" }}>
          <KpiTile label="Combustível" value={s ? fmtBRL(s.fuelCost) : "—"}
            visual={monthlyFuel.length >= 2 ? <SparkLine values={monthlyFuel} label="Combustível por mês" /> : undefined}
            footnote={s ? pctOfTotal(s.fuelCost, s.totalCost) : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "frota.manutencao", from, to, expected: s?.maintenanceCost, title: "Manutenção" }}>
          <KpiTile label="Manutenção" value={s ? fmtBRL(s.maintenanceCost) : "—"}
            visual={monthlyMaintenance.length >= 2 ? <SparkLine values={monthlyMaintenance} label="Manutenção por mês" /> : undefined}
            footnote={s ? pctOfTotal(s.maintenanceCost, s.totalCost) : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "frota.pneus", from, to, expected: s?.tireCost, title: "Pneus" }}>
          <KpiTile label="Pneus" value={s ? fmtBRL(s.tireCost) : "—"}
            visual={monthlyTires.length >= 2 ? <SparkLine values={monthlyTires} label="Pneus por mês" /> : undefined}
            footnote={s ? pctOfTotal(s.tireCost, s.totalCost) : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "frota.custo-km", from, to, expected: s?.costPerKm, title: "Custo / KM da Frota" }}>
          <KpiTile label="Custo / KM da Frota" value={s ? `R$ ${fmt2(s.costPerKm)}` : "—"}
            footnote="total ÷ km" loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "frota.veiculo-mais-caro", from, to, expected: s?.mostExpensiveCost, title: "Veículo Mais Caro" }}>
          <KpiTile label="Veículo Mais Caro" value={s?.mostExpensivePlate ?? "—"}
            footnote={s?.mostExpensivePlate ? `${fmtBRL(s.mostExpensiveCost)} · maior custo total` : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
      </div>

      {/* Pontos de atenção + composição + evolução */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cabeçalho no mesmo padrão do ChartCard (header separado por borda,
            título + subtítulo) — antes era um <p> colado direto no conteúdo,
            sem o respiro dos 2 cards vizinhos desta linha (que usam ChartCard).
            Precisa do subtítulo também pra altura do cabeçalho bater com a dos
            vizinhos (eles têm subtítulo; sem um aqui, a borda ficava mais alta
            que a dos outros 2, desalinhada na mesma linha do grid). */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-amber-500" />
              Pontos de atenção da frota ({alerts.length})
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Pneus, custo/km e variação de custo fora do padrão</p>
          </div>
          <div className="p-5 space-y-2.5">
            {alerts.length === 0 ? (
              <p className="text-sm text-gray-400">Sem pontos de atenção no período.</p>
            ) : alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-500" : "bg-blue-500"}`} />
                <div>
                  <p className="font-medium text-gray-800">{a.title}</p>
                  <p className="text-xs text-gray-400">{a.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <ChartCard title="Composição do custo da frota" subtitle="Participação por categoria" height={200}
          isEmpty={!isLoading && composition.every((c) => c.cost === 0)} loading={isLoading}>
          <div className="space-y-4 pt-2">
            {composition.map((c) => (
              <div key={c.category}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{c.category}</span>
                  <span className="font-semibold text-gray-900">{fmtBRL(c.cost)} <span className="text-gray-400 font-normal">({Math.round(c.pct * 100)}%)</span></span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.round(c.pct * 100)}%`, background: CAT_COLOR[c.category] ?? "var(--brand)" }} />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Evolução do custo total" subtitle="Por mês, nos últimos 5 meses" height={200}
          isEmpty={!isLoading && costTrend.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costTrend} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fleet-fuel" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-fleet-maintenance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#DAA72B" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#DAA72B" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-fleet-tires" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#3B82F6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number, n: string) => [fmtBRL(v), n === "fuel" ? "Combustível" : n === "tires" ? "Pneus" : "Manutenção"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Legend formatter={(n) => (n === "fuel" ? "Combustível" : n === "tires" ? "Pneus" : "Manutenção")} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="fuel" stackId="a" fill="url(#grad-fleet-fuel)" />
              <Bar dataKey="maintenance" stackId="a" fill="url(#grad-fleet-maintenance)" />
              <Bar dataKey="tires" stackId="a" fill="url(#grad-fleet-tires)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top 10 + scatter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top 10 veículos por custo total" subtitle="Ranking por custo consolidado (R$)" height={300}
          isEmpty={!isLoading && top10.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ top: 0, right: 60, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fleet-top10" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.95} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="plate" tick={{ fontSize: 11, fill: "#6b7280" }} tickLine={false} axisLine={false} width={72} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Custo total"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="total" fill="url(#grad-fleet-top10)" radius={[0, 4, 4, 0]}
                label={{ position: "right", formatter: (v: number) => fmtBRL(v), fontSize: 10, fill: "#6b7280" }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Custo por km × Faturamento" subtitle="Cada ponto representa um veículo com pelo menos 1.000 km no período" height={300}
          isEmpty={!isLoading && scatter.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" dataKey="x" name="Faturamento" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              {/* " " em vez de espaço normal entre "R$" e o número: espaço
                  comum faz o Recharts quebrar linha no rótulo do eixo. */}
              <YAxis type="number" dataKey="y" name="Custo/km" tickFormatter={(v) => `R$ ${fmt2(v)}`}
                tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} width={64} />
              {fleetCpk > 0 && <ReferenceLine y={fleetCpk} stroke="#9ca3af" strokeDasharray="6 4"
                label={{ value: `Média R$ ${fmt2(fleetCpk)}`, position: "insideTopRight", fontSize: 10, fill: "#9ca3af" }} />}
              <Tooltip cursor={{ strokeDasharray: "3 3" }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                formatter={(v: number, n: string) => n === "Faturamento" ? [fmtBRL(v), "Faturamento"] : [`R$ ${fmt2(v)}`, "Custo/km"]}
                labelFormatter={() => ""} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Scatter name="Abaixo da média (melhor)" data={scatterBelow} fill="var(--brand)" />
              <Scatter name="Acima da média (pior)" data={scatterAbove} fill="#E5484D" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Ranking detalhado por placa */}
      <TollCoverageNotice coverage={data?.tollCoverage} />
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Ranking detalhado por placa</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Ordenado pelo <strong>maior faturamento</strong> · placas <strong>sem faturamento no período</strong> caem para o fim ·
            a linha é o <strong>conjunto</strong>: a receita e o custo da carreta vinculada (cadastro Sinersoft ou
            reboque da viagem) entram no cavalo que a puxa. Por isso o faturamento aqui pode ser maior que o da
            mesma placa na tela Operacional CT-e, que não consolida · % é a participação no faturamento ·
            para ordenar pela pior margem, use a tela Margem por Placa
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-surface-border">
                <th className="px-5 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Motorista</th>
                <th className="px-3 py-2 text-right font-medium">Faturamento</th>
                <th className="px-3 py-2 text-right font-medium">Combustível</th>
                <th className="px-3 py-2 text-right font-medium">Manutenção</th>
                <th className="px-3 py-2 text-right font-medium">Pneus</th>
                <th className="px-3 py-2 text-right font-medium">Salário motorista</th>
                <th className="px-3 py-2 text-right font-medium">Pedágio</th>
                <th className="px-3 py-2 text-right font-medium">Custo direto</th>
                <th className="px-5 py-2 text-right font-medium">Margem</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={10} className="px-5 py-6 text-center text-gray-400">Carregando…</td></tr>
              ) : vehicles.length === 0 ? (
                <tr><td colSpan={10} className="px-5 py-6 text-center text-gray-400">Sem dados no período.</td></tr>
              ) : list.visible.map((v) => (
                <tr key={v.plate} className="border-b border-surface-border/60 hover:bg-gray-50 align-top">
                  <td className="px-5 py-2.5">
                    <Link to={`/frota/placa/${encodeURIComponent(v.plate)}`} className="font-medium text-tm-700 hover:underline">{v.plate}</Link>
                    {v.brand && <p className="text-xs text-gray-400">{v.brand}</p>}
                    {v.trailers.length > 0 && (
                      <p className="text-xs text-blue-600 mt-0.5">
                        + carreta {v.trailers.join(" · ")}
                      </p>
                    )}
                    {v.isTrailer ? (
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px]">
                        carreta sem cavalo vinculado
                      </span>
                    ) : v.idle && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]">
                        sem movimento no período
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{v.driver ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-gray-900">
                    {v.revenue > 0 ? (
                      <>
                        <p>{v.revenueFormatted}</p>
                        <div className="h-1 w-20 ml-auto mt-1.5 rounded-full bg-gray-100 overflow-hidden" role="img"
                          aria-label={`${Math.round((v.revenue / maxRevenue) * 100)}% do maior faturamento da lista`}>
                          <div className="h-full rounded-full bg-tm-400" style={{ width: `${Math.min(100, Math.round((v.revenue / maxRevenue) * 100))}%` }} />
                        </div>
                      </>
                    ) : "—"}
                  </td>
                  <CostCell value={v.fuel} revenue={v.revenue} />
                  <CostCell value={v.maintenance} revenue={v.revenue} />
                  <CostCell value={v.tires} revenue={v.revenue} />
                  <CostCell value={v.driverPay + v.commission} revenue={v.revenue} />
                  <CostCell value={v.tolls} revenue={v.revenue} semExtrato={v.tollsSource === "sem_extrato"} />
                  <td className="px-3 py-2.5 text-right">
                    <p className="font-semibold text-gray-900">{v.directCostFormatted}</p>
                    <p className="text-xs text-gray-400">
                      {v.revenue > 0 ? `${fmtPct((v.directCost / v.revenue) * 100)} do faturamento` : "—"}
                    </p>
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <p className={`font-semibold ${v.margin < 0 ? "text-danger-600" : "text-gray-900"}`}>{v.marginFormatted}</p>
                    <p className={`text-xs ${v.marginPct != null && v.marginPct < 0 ? "text-danger-500" : "text-gray-400"}`}>{v.marginPct != null ? fmtPct(v.marginPct * 100) : "—"}</p>
                  </td>
                </tr>
              ))}
              {!isLoading && <LoadMoreRow list={list} colSpan={10} label="placas" />}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
